import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { DateTime } from 'luxon'
import type { SlotDisponible } from '@practiclinic/core'
import { db } from '../datos/db.js'
import { uuidv7 } from '../datos/uuid.js'
import { encolar } from '../datos/sync.js'
import { aCuerpoAsignacion, slotsLocales } from '../datos/motor.js'
import { buscarClientes } from '../datos/consultas.js'
import { guaranies, hora, ZONA } from '../datos/formato.js'
import { Panel } from '../comunes/Panel.js'
import { Boton } from '../comunes/Boton.js'

/**
 * Agendar una cita.
 *
 * El orden de los pasos no es arbitrario: **servicio primero, paciente después**.
 * El servicio define cuánto dura y qué recursos necesita, así que sin él no hay
 * horarios que ofrecer. Preguntar el nombre antes obliga a volver atrás cuando
 * el horario que quería no existe.
 *
 * Los horarios los calcula el motor **en este dispositivo**, contra Dexie: la
 * recepción tiene que poder agendar con internet caído (§5.2). Que un slot se
 * ofrezca acá no garantiza que esté libre al llegar al servidor —otro
 * dispositivo pudo tomarlo— y eso está bien: la verdad la impone el constraint
 * de exclusión, y si pierde, la cita vuelve como conflicto a la bandeja (§5.3).
 */

export interface NuevaCitaProps {
  /** El recurso de la columna donde se hizo clic. Se usa como preferido. */
  recursoId: string
  /** `YYYY-MM-DDTHH:MM` en hora local: donde cayó el clic. */
  inicioSugerido: string
  clientePrefijado?: string | null
  onCerrar: () => void
}

export function NuevaCita({ recursoId, inicioSugerido, clientePrefijado, onCerrar }: NuevaCitaProps) {
  const [servicioId, setServicioId] = useState('')
  const [clienteId, setClienteId] = useState<string | null>(clientePrefijado ?? null)
  const [slotElegido, setSlotElegido] = useState<SlotDisponible | null>(null)
  const [slots, setSlots] = useState<SlotDisponible[] | null>(null)
  const [guardando, setGuardando] = useState(false)

  const servicios = useLiveQuery(() => db.servicio.filter((s) => s.activo).toArray(), [], [])
  const recurso = useLiveQuery(() => db.recurso.get(recursoId), [recursoId])

  const momento = useMemo(
    () => DateTime.fromISO(inicioSugerido, { zone: ZONA }).toUTC(),
    [inicioSugerido],
  )

  // Se ofrece una ventana alrededor del clic, no sólo esa hora exacta: si el
  // horario pedido está tomado, lo útil es ver el de al lado, no un "no hay".
  useEffect(() => {
    if (!servicioId) {
      setSlots(null)
      setSlotElegido(null)
      return
    }
    let vigente = true
    const desde = momento.minus({ hours: 2 }).toJSDate()
    const hasta = momento.plus({ hours: 6 }).toJSDate()

    void slotsLocales({ servicioId, desde, hasta, recursoPreferido: recursoId }).then((res) => {
      if (!vigente) return
      setSlots(res)
      // Si el horario donde se hizo clic está disponible, ya queda elegido: es
      // lo que la persona pidió.
      const exacto = res.find((s) => s.inicio.getTime() === momento.toMillis())
      setSlotElegido(exacto ?? res[0] ?? null)
    })
    return () => {
      vigente = false
    }
  }, [servicioId, momento, recursoId])

  async function agendar() {
    if (!servicioId || !clienteId || !slotElegido) return
    setGuardando(true)

    const citaId = uuidv7()
    const asignacion = aCuerpoAsignacion(slotElegido)

    // Optimista: la cita aparece en la grilla al instante y marcada como
    // tentativa hasta que el servidor la confirme (§5.3).
    await db.transaction('rw', [db.cita, db.cita_tramo, db.reserva_recurso], async () => {
      await db.cita.add({
        id: citaId,
        cliente_id: clienteId,
        estado: 'confirmada',
        origen: 'recepcion',
        notas: null,
        tentativa_local: true,
        updated_at: new Date().toISOString(),
        deleted_at: null,
      })
      for (const tramo of slotElegido.tramos) {
        const tramoId = uuidv7()
        await db.cita_tramo.add({
          id: tramoId,
          cita_id: citaId,
          orden: tramo.orden,
          inicio: tramo.rango.inicio.toISOString(),
          fin: tramo.rango.fin.toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null,
        })
        for (const r of tramo.recursos) {
          await db.reserva_recurso.add({
            id: uuidv7(),
            cita_tramo_id: tramoId,
            recurso_id: r.recursoId,
            rango: { inicio: r.rango.inicio.toISOString(), fin: r.rango.fin.toISOString() },
            activa: true,
            updated_at: new Date().toISOString(),
            deleted_at: null,
          })
        }
      }
    })

    await encolar('crear_cita', {
      citaId,
      clienteId,
      servicioId,
      estado: 'confirmada',
      asignacion,
    })
    setGuardando(false)
    onCerrar()
  }

  const listo = Boolean(servicioId && clienteId && slotElegido)

  return (
    <Panel
      titulo="Nueva cita"
      {...(recurso
        ? { bajada: `${recurso.nombre} · ${hora(momento.toISO() ?? inicioSugerido)}` }
        : {})}
      onCerrar={onCerrar}
      pie={
        <div className="flex items-center justify-between gap-3">
          <span className="txt-meta">
            {slotElegido
              ? `${hora(slotElegido.inicio.toISOString())}–${hora(slotElegido.fin.toISOString())}`
              : 'elegí un horario'}
          </span>
          <Boton variante="primary" disabled={!listo || guardando} onClick={() => void agendar()}>
            {guardando ? 'Agendando…' : 'Agendar'}
          </Boton>
        </div>
      }
    >
      <Paso numero={1} titulo="¿Qué se le va a hacer?">
        <select
          value={servicioId}
          onChange={(e) => setServicioId(e.target.value)}
          autoFocus
          className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
        >
          <option value="">Elegí el servicio…</option>
          {servicios.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nombre} — {guaranies(s.precio)}
            </option>
          ))}
        </select>
      </Paso>

      {servicioId && (
        <Paso numero={2} titulo="¿A qué hora?">
          {slots === null ? (
            <p className="txt-secundario">Buscando horarios…</p>
          ) : slots.length === 0 ? (
            <p className="txt-secundario">
              No hay lugar cerca de esa hora con ese profesional. Probá otro día o soltá el
              filtro eligiendo desde la agenda de otro.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.slice(0, 12).map((s) => {
                const elegido = slotElegido?.inicio.getTime() === s.inicio.getTime()
                return (
                  <button
                    key={s.inicio.toISOString()}
                    type="button"
                    onClick={() => setSlotElegido(s)}
                    className={`h-9 rounded-lg border px-3 text-[13px] tabular-nums transition ${
                      elegido
                        ? 'border-teal-700 bg-teal-700 font-medium text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {hora(s.inicio.toISOString())}
                  </button>
                )
              })}
            </div>
          )}
        </Paso>
      )}

      {servicioId && slotElegido && (
        <Paso numero={3} titulo="¿Para quién?">
          <ElegirPaciente clienteId={clienteId} onElegir={setClienteId} />
        </Paso>
      )}
    </Panel>
  )
}

function Paso({
  numero,
  titulo,
  children,
}: {
  numero: number
  titulo: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="txt-body mb-2 font-medium text-slate-900">
        <span className="txt-meta mr-2">{numero}</span>
        {titulo}
      </h3>
      {children}
    </section>
  )
}

/**
 * Buscar el paciente o crearlo en el momento.
 *
 * El alta va acá y no en otra pantalla porque el caso real es el teléfono
 * sonando: la persona no está en el sistema y hay que agendarla igual, sin
 * cortar para ir a "Pacientes" y volver.
 */
function ElegirPaciente({
  clienteId,
  onElegir,
}: {
  clienteId: string | null
  onElegir: (id: string) => void
}) {
  const [texto, setTexto] = useState('')
  const [creando, setCreando] = useState(false)
  const [telefono, setTelefono] = useState('')

  const encontrados = useLiveQuery(() => buscarClientes(texto), [texto], [])
  const elegido = useLiveQuery(async () => {
    if (!clienteId) return undefined
    return db.cliente.get(clienteId)
  }, [clienteId])

  if (elegido) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="txt-body truncate font-medium text-slate-900">{elegido.nombre}</p>
          <p className="txt-meta truncate">{elegido.telefono ?? 'sin teléfono'}</p>
        </div>
        <Boton variante="ghost" tamano="sm" onClick={() => onElegir('')}>
          Cambiar
        </Boton>
      </div>
    )
  }

  async function crear() {
    const nombre = texto.trim()
    if (nombre.length < 2) return
    const id = uuidv7()
    await db.cliente.add({
      id,
      nombre,
      doc: null,
      telefono: telefono.replace(/\D/g, '') || null,
      cobertura_id: null,
      updated_at: new Date().toISOString(),
      deleted_at: null,
    })
    await encolar('crear_cliente', {
      clienteId: id,
      nombre,
      telefono: telefono.replace(/\D/g, '') || null,
    })
    onElegir(id)
    setCreando(false)
  }

  return (
    <div>
      <input
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Buscar por nombre, teléfono o documento"
        className="h-9 w-full rounded-lg border border-slate-300 px-3 text-[14px]"
      />

      {texto.trim().length >= 2 && (
        <>
          {encontrados.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
              {encontrados.slice(0, 6).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onElegir(c.id)}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="txt-body block truncate font-medium text-slate-900">
                      {c.nombre}
                    </span>
                    <span className="txt-meta block truncate">
                      {c.telefono ?? c.doc ?? 'sin contacto'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {creando ? (
            <div className="mt-2 rounded-lg border border-slate-200 p-3">
              <p className="txt-secundario mb-2">
                Se crea <strong className="text-slate-900">{texto.trim()}</strong>
              </p>
              <label className="block">
                <span className="txt-meta block">Teléfono (opcional)</span>
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="0981 123 456"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-3 text-[14px]"
                />
              </label>
              <div className="mt-3 flex gap-2">
                <Boton variante="primary" tamano="sm" onClick={() => void crear()}>
                  Crear y elegir
                </Boton>
                <Boton variante="ghost" tamano="sm" onClick={() => setCreando(false)}>
                  Cancelar
                </Boton>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className="txt-meta mt-2 underline decoration-dotted hover:text-slate-600"
            >
              {encontrados.length > 0 ? 'No es ninguno: crear paciente nuevo' : 'Crear paciente nuevo'}
            </button>
          )}
        </>
      )}
    </div>
  )
}
